import { mixpanelReset, mxp } from '@analytics/src/mixpanel';
import analyticsAPI from '@api/jooicez-backend/analytics';
import { UserCountry } from '@models/abTest/experiment';
import { OutsetaCountryEnum } from '@models/common/outsetaRealm.enum';
import { EAccountStageLabel, User } from '@models/outseta/outseta';
import { EOutsetaEvents } from '@models/outseta/outsetaEvents';
import { buildDemoUser, isDemoMode } from '@utils/demoMode';
import { initializeOutsetaProfile } from '@utils/outseta';
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { isClerkAuthEnabled, signOutClerk } from 'src/utils/clerkAuth';
import localStorageKeys from 'src/utils/localStorageKeys';

import { AuthContext, AuthProviderValue } from './auth-context';

export interface myWindow extends Window {
  Outseta: unknown;
}

const OutsetaContext = createContext<AuthProviderValue | undefined>(undefined);

export const AuthProvider: React.FC<OutsetaProviderProps> = ({ children }) => {
  return <OutsetaProvider>{children}</OutsetaProvider>;
};

interface OutsetaProviderProps {
  children: React.ReactNode;
}

export interface OutsetaRefI {
  setAccessToken: (param: string) => void;
  getUser: () => Promise<User>;
  on: (param1: string, param2: unknown) => void;
  profile: {
    open: (param: { tab: string }) => void;
  };
}

enum EAuthStatus {
  INIT = 'init',
  READY = 'ready',
}

export const OutsetaProvider: React.FC<OutsetaProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<EAuthStatus>(EAuthStatus.INIT);
  const [user, setUser] = useState<User | null>(null);
  const [countryCode, setCountryCode] = useState<UserCountry | null>(null);
  const [jooiceFlow, setJooiceFlow] = useState<string | null>(null);
  const outsetaRef = useRef<OutsetaRefI | null>(null);
  const userFetchedRef = useRef(false);
  const eventHandlersInitialized = useRef(false);
  const isInitialized = useRef(false);

  const updateUser = useCallback(
    async (forceUpdate = false) => {
      if ((!forceUpdate && userFetchedRef.current && user) || !outsetaRef.current) return;

      try {
        const outsetaUser = await outsetaRef.current.getUser();

        if (JSON.stringify(user) !== JSON.stringify(outsetaUser)) {
          setUser(outsetaUser);
          const outsetaCountry =
            (localStorage.getItem(localStorageKeys.commons.countryPreference) as UserCountry) ?? UserCountry.ITA;
          mxp.identify(outsetaUser.Email, {
            billing_stage: outsetaUser.Account.AccountStageLabel,
            plan_id: outsetaUser.Account.CurrentSubscription.Plan.Uid,
            plan_type: outsetaUser.Account.CurrentSubscription.Plan.Name,
            renewal_terms: outsetaUser.Account.CurrentSubscription.BillingRenewalTerm,
            outseta_country: outsetaCountry,
            is_spartacus: outsetaCountry === UserCountry.SPARTACUS,
            trial_status: outsetaUser.Account.AccountStageLabel === 'Trialing' ? 'active' : 'inactive',
            assigned_flow: outsetaUser.Account?.JooiceFlow as 'review' | 'social',
          });
          // num_location / location_tier are BE-computed (active locations across the
          // account's orgs) — fetch + register them as super-props, best-effort.
          analyticsAPI
            .getTraits()
            .then((traits) =>
              mxp.register({
                is_spartacus: traits.is_spartacus,
                num_location: traits.num_location,
                location_tier: traits.location_tier,
              }),
            )
            .catch(() => {
              // analytics must never break the auth flow
            });
        }

        userFetchedRef.current = true;
        setStatus(EAuthStatus.READY);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setStatus(EAuthStatus.READY);
      }
    },
    [user],
  );

  const handleOutsetaUserEvents = useCallback(
    (onEvent: (event: EOutsetaEvents, props: unknown[]) => void) => {
      if (!outsetaRef.current || eventHandlersInitialized.current) return;

      const Outseta = outsetaRef.current;

      Object.values(EOutsetaEvents).forEach((event) => {
        if (Outseta?.on) {
          Outseta.on(event, (...props: unknown[]) => {
            onEvent(event, props);
            if ([EOutsetaEvents.SUBSCRIPTION_UPDATE, EOutsetaEvents.PROFILE_UPDATE].includes(event as EOutsetaEvents)) {
              updateUser(true);
            }
          });
        }
      });

      eventHandlersInitialized.current = true;
    },
    [updateUser],
  );

  useEffect(() => {
    if (isInitialized.current) return;

    // Demo mode: the mock backend's token is not a real Outseta session, and the SDK
    // would hang on it (see utils/demoMode.ts). Build the user from the token instead
    // and go straight to READY — no script, no network, no Outseta.
    if (isDemoMode()) {
      const token = localStorage.getItem('Outseta.nocode.accessToken');
      if (token) {
        try {
          setUser(buildDemoUser(token));
        } catch (error) {
          console.error('Failed to build the demo user:', error);
        }
      }
      setCountryCode((localStorage.getItem(localStorageKeys.commons.countryPreference) as UserCountry) ?? null);
      setStatus(EAuthStatus.READY);
      isInitialized.current = true;
      return;
    }

    const initOutseta = async () => {
      try {
        const countryCode = localStorage.getItem(localStorageKeys.commons.countryPreference);
        if (!countryCode) {
          throw new Error('Country code not found');
        }
        setCountryCode(countryCode as UserCountry);
        await initializeOutsetaProfile(countryCode);

        const outseta = (window as unknown as myWindow).Outseta;
        if (!outseta) {
          throw new Error('Outseta not initialized');
        }

        outsetaRef.current = outseta as OutsetaRefI;
        const token = localStorage.getItem('Outseta.nocode.accessToken');

        if (token) {
          outsetaRef.current.setAccessToken(token);
          handleOutsetaUserEvents((event) => {
            if ([EOutsetaEvents.SUBSCRIPTION_UPDATE, EOutsetaEvents.PROFILE_UPDATE].includes(event)) {
              updateUser(true);
            }
            // Note: 'Payment Submitted' is fired server-side (policy Spartacus
            // payment-succeeded webhook) — authoritative + ad-blocker-immune.
            // Not fired from the FE Outseta widget events to avoid double-count
            // and wrong semantics (those events = info/subscription update, not a payment).
          });
          await updateUser();
        }

        setStatus(EAuthStatus.READY);
        isInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize Outseta:', error);
        setStatus(EAuthStatus.READY);
        isInitialized.current = true;
      }
    };

    initOutseta();
  }, [handleOutsetaUserEvents, updateUser]);

  const logout = async (reason?: 'token_expired' | 'manual', error?: number) => {
    // Clerk owns session expiry. Automatic token-expiry handling must not clear
    // storage or redirect while Clerk still controls session state.
    if (isClerkAuthEnabled() && (reason ?? 'manual') !== 'manual') return;

    if ((reason ?? 'manual') === 'manual' && isClerkAuthEnabled()) {
      await signOutClerk();
    }

    // Drop the analytics identity so the next login doesn't inherit it
    mixpanelReset();
    // Unset access token (in demo mode there is no SDK instance to clear)
    if (outsetaRef.current) {
      outsetaRef.current.setAccessToken('');
    }

    // Get country code from localStorage before clearing it
    const outsetaCountry = localStorage.getItem(localStorageKeys.commons.countryPreference);
    const countryCode = outsetaCountry === OutsetaCountryEnum.SPARTACUS ? OutsetaCountryEnum.USA : outsetaCountry;

    // Clear storage. Cookie consent is unaffected: Iubenda stores it in a cookie.
    localStorage.clear();
    sessionStorage.clear();

    // Set logout reason
    sessionStorage.setItem('logout_reason', reason || 'manual');
    const countryQuery = countryCode && countryCode !== 'null' ? `country=${countryCode}` : '';
    const errorQuery = error ? `error=${error}` : '';
    const query = [countryQuery, errorQuery].filter(Boolean).join('&');
    window.location.href = `/auth${query ? `?${query}` : ''}`;
  };

  const accountStageLabel = (user?.Account.AccountStageLabel || '').trim().toLowerCase();
  const hasUnsubscribed =
    accountStageLabel === EAccountStageLabel.UNSUBSCRIBED.toLowerCase() || accountStageLabel === 'cancelling trial';
  const isTrial = accountStageLabel === EAccountStageLabel.TRIAL.toLowerCase();

  const value = {
    user,
    hasUnsubscribed,
    isTrial,
    countryCode,
    isLoading: status !== EAuthStatus.READY,
    logout,
    jooiceFlow,
    setJooiceFlow,
  };

  return (
    <AuthContext.Provider value={value}>
      <OutsetaContext.Provider value={value}>{children}</OutsetaContext.Provider>
    </AuthContext.Provider>
  );
};

export const useOutseta = () => {
  const context = useContext(OutsetaContext);
  if (context === undefined) {
    throw new Error('useOutseta must be used within an OutsetaProvider');
  }
  return context;
};
